// src/pages/visits/VisitList.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";
import VisitsMapModal from "./visitMap";


/* ---------- helpers ---------- */
function toISODate(d) {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}
function fmtDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
}
function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function decodeJwtUnsafe(token) {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

export default function VisitList() {
  const token = localStorage.getItem("token") || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const me = useMemo(() => decodeJwtUnsafe(token), [token]); // { id, role, name? }
const [showMap, setShowMap] = useState(false);

  // filters (defaults to today)
  const [from, setFrom] = useState(() => toISODate(new Date()));
  const [to, setTo] = useState(() => toISODate(new Date()));
  const [q, setQ] = useState("");
  const [onlySelfies, setOnlySelfies] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false); // NEW: filter “my visits”
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [salespersonId, setSalespersonId] = useState(""); // 🔥 NEW
  const [salespersons, setSalespersons] = useState([]); // 🔥 NEW


  // data & ui
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [serverTotalKm, setServerTotalKm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / limit));

  function normalize(list = []) {
    return list
      .map((v) => ({
        id: v.id,
        when: v.visited_at || v.visit_time || v.created_at || v.updated_at,
        customer_name: v.customer_name || v.customer || "",
        salesperson_name: v.salesperson_name || v.user_name || v.salesperson || "",
        lat: typeof v.latitude === "string" ? parseFloat(v.latitude) : v.latitude,
        lng: typeof v.longitude === "string" ? parseFloat(v.longitude) : v.longitude,
        accuracy_m: v.accuracy_m ?? null,
        distance_km: v.distance_km ?? null,
        remarks: v.remarks || v.notes || "",
        photo_url: v.photo_url || v.selfie_url || null,
      }))
      .sort((a, b) => new Date(b.when) - new Date(a.when));
  }

  async function fetchList() {
    setLoading(true);
    setError("");
    setServerTotalKm(null);
    try {
      // Use the ADMIN/LIST endpoint which supports pagination and filters.
      const params = {
        from,
        to,
        q,
        selfieOnly: onlySelfies ? 1 : undefined,
        page,
        limit,
        // For admins, “mine=1” forces own scope. For non-admins, backend already scopes.
        mine: onlyMine ? 1 : undefined,
        salesperson_id: salespersonId || undefined, // 🔥 NEW

        // Alternatively, if you prefer explicit user_id:
        // user_id: onlyMine && me?.id ? me.id : undefined,
      };
      const res = await axios.get(`${API_BASE_URL}/api/visit/list`, {
        params,
        headers,
      });
      const data = res.data || {};
      setRows(normalize(data.rows || []));
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.error(err);
      // Fallback to legacy “my” route (no pagination). Still respects date range client-side.
      try {
        const r3 = await axios.get(`${API_BASE_URL}/api/visit/my`, { headers });
        const arr = Array.isArray(r3.data) ? r3.data : [];
        const norm = normalize(arr);
        const fromD = new Date(`${from}T00:00:00`);
        const toD = new Date(`${to}T23:59:59`);
        const filtered = norm.filter((x) => {
          const d = new Date(x.when);
          return d >= fromD && d <= toD;
        });
        setRows(filtered);
        setTotal(filtered.length);
      } catch (e2) {
        console.error(e2);
        setError("Failed to load visits.");
      }
    } finally {
      setLoading(false);
    }
  }

  // reload on filter/page changes
  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, q, onlySelfies, onlyMine, salespersonId, page, limit]);

  // totals
  const computedTotalKm = useMemo(() => {
    if (serverTotalKm != null) return serverTotalKm;
    const listAsc = [...rows].sort(
      (a, b) => new Date(a.when) - new Date(b.when)
    );
    let sum = 0;
    for (let i = 1; i < listAsc.length; i++) {
      const p = listAsc[i - 1];
      const c = listAsc[i];
      if (
        typeof p.lat === "number" &&
        typeof p.lng === "number" &&
        typeof c.lat === "number" &&
        typeof c.lng === "number"
      ) {
        sum += haversineKm(p.lat, p.lng, c.lat, c.lng);
      }
    }
    return Math.round(sum * 100) / 100;
  }, [rows, serverTotalKm]);

  const exportCSV = () => {
    const cols = [
      "DateTime",
      "Customer",
      "Salesperson",
      "Latitude",
      "Longitude",
      "Accuracy(m)",
      "Distance(km)",
      "Remarks",
      "PhotoURL",
    ];
    const lines = [cols.join(",")];
    rows.forEach((r) => {
      const photoAbs = r.photo_url
        ? r.photo_url.startsWith("http")
          ? r.photo_url
          : `${API_BASE_URL}${r.photo_url.startsWith("/") ? r.photo_url : "/" + r.photo_url}`
        : "";
      const row = [
        fmtDateTime(r.when),
        (r.customer_name || "").replaceAll(",", " "),
        (r.salesperson_name || "").replaceAll(",", " "),
        r.lat ?? "",
        r.lng ?? "",
        r.accuracy_m ?? "",
        r.distance_km ?? "",
        (r.remarks || "").replaceAll(",", " "),
        photoAbs,
      ];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `visits_${onlyMine ? "mine_" : ""}${from}_to_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const quickSet = (type) => {
    const now = new Date();
    if (type === "today") {
      const d = toISODate(now);
      setFrom(d); setTo(d); setPage(1);
    } else if (type === "yesterday") {
      const y = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const d = toISODate(y);
      setFrom(d); setTo(d); setPage(1);
    } else if (type === "week") {
      const toD = toISODate(now);
      const fromD = toISODate(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000));
      setFrom(fromD); setTo(toD); setPage(1);
    } else if (type === "month") {
      const toD = toISODate(now);
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      setFrom(toISODate(start)); setTo(toD); setPage(1);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-2xl font-semibold">
          {onlyMine ? "My Visits" : "All Visits"}
        </h1>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-slate-600">From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="rounded-md border px-2 py-1" />
          </div>
          <div>
            <label className="block text-xs text-slate-600">To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="rounded-md border px-2 py-1" />
          </div>
          <div>
            <label className="block text-xs text-slate-600">Customer</label>
            <input placeholder="Search name…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="rounded-md border px-2 py-1" />
          </div>
          {/* 🔥 Salesperson filter */}
        {me.role === "admin" && (
          <div>
            <label className="text-xs">Salesperson</label>
            <select
              value={salespersonId}
              onChange={(e) => {
                setSalespersonId(e.target.value);
                setPage(1);
              }}
              className="border px-2 py-1 rounded-md"
            >
              <option value="">All</option>
              {salespersons.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}
          <label className="inline-flex items-center gap-2 text-sm ml-2">
            <input type="checkbox" checked={onlySelfies} onChange={(e) => { setOnlySelfies(e.target.checked); setPage(1); }} />
            Only with selfie
          </label>
          <label className="inline-flex items-center gap-2 text-sm ml-2">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => { setOnlyMine(e.target.checked); setPage(1); }}
            />
            My visits only
          </label>

          <Link to="/visits/mark" className="rounded-md bg-green-600 px-3 py-2 text-white hover:bg-green-700">
            + Mark Visit
          </Link>

          <button onClick={fetchList} className="ml-2 rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700">
            Refresh
          </button>
          {/* <button onClick={exportCSV} className="rounded-md border px-3 py-2 hover:bg-slate-50">
            Export CSV
          </button> */}
          <button onClick={() => setShowMap(true)} disabled={!rows.length} className="border px-3 py-2 rounded-md">
          View Visits Map
        </button>
  <div className="text-sm mb-2 text-slate-600">
        Total distance (visible): <strong>{computedTotalKm.toFixed(2)} km</strong>
      </div>

      <VisitsMapModal open={showMap} onClose={() => setShowMap(false)} visits={rows} />

        </div>
        
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <button className="rounded-full border px-3 py-1 hover:bg-slate-50" onClick={() => quickSet("today")}>Today</button>
        <button className="rounded-full border px-3 py-1 hover:bg-slate-50" onClick={() => quickSet("yesterday")}>Yesterday</button>
        <button className="rounded-full border px-3 py-1 hover:bg-slate-50" onClick={() => quickSet("week")}>Last 7 days</button>
        <button className="rounded-full border px-3 py-1 hover:bg-slate-50" onClick={() => quickSet("month")}>This month</button>
        <span className="ml-auto text-slate-600">
          Total distance (visible rows): <strong>{computedTotalKm.toFixed(2)} km</strong>
        </span>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-left">
            <tr className="text-slate-700">
              <th className="px-3 py-2">Date/Time</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Salesperson</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Accuracy</th>
              <th className="px-3 py-2">Distance</th>
              <th className="px-3 py-2">Selfie</th>
              <th className="px-3 py-2">Remarks</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-4" colSpan={9}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="px-3 py-4" colSpan={9}>No visits found</td></tr>
            ) : (
              rows.map((r) => {
                const photoAbs = r.photo_url
                  ? r.photo_url.startsWith("http")
                    ? r.photo_url
                    : `${API_BASE_URL}${r.photo_url.startsWith("/") ? r.photo_url : "/" + r.photo_url}`
                  : null;
                const gmaps =
                  typeof r.lat === "number" && typeof r.lng === "number"
                    ? `https://www.google.com/maps?q=${r.lat},${r.lng}`
                    : null;
                return (
                  <tr key={r.id} className="border-top">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(r.when)}</td>
                    <td className="px-3 py-2">{r.customer_name || "-"}</td>
                    <td className="px-3 py-2">{r.salesperson_name || "-"}</td>
                    <td className="px-3 py-2">
                      {typeof r.lat === "number" && typeof r.lng === "number" ? (
                        <span className="font-mono text-xs">{r.lat.toFixed(6)}, {r.lng.toFixed(6)}</span>
                      ) : (<span className="text-slate-500">—</span>)}
                    </td>
                    <td className="px-3 py-2">{r.accuracy_m != null ? `${r.accuracy_m} m` : "—"}</td>
                    <td className="px-3 py-2">{r.distance_km != null ? `${r.distance_km} km` : "—"}</td>
                    <td className="px-3 py-2">
                      {photoAbs ? (
                        <a href={photoAbs} target="_blank" rel="noreferrer">
                          <img src={photoAbs} alt="selfie" className="h-12 w-12 rounded-md object-cover" />
                        </a>
                      ) : (<span className="text-slate-500">—</span>)}
                    </td>
                    <td className="px-3 py-2 max-w-[30ch] truncate" title={r.remarks}>{r.remarks || "—"}</td>
                    <td className="px-3 py-2">
                      {gmaps ? (
                        <a href={gmaps} target="_blank" rel="noreferrer" className="rounded-md border px-2 py-1 hover:bg-slate-50">
                          View Map
                        </a>
                      ) : (<span className="text-slate-500">—</span>)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="mt-3 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="text-slate-600">Rows per page</span>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            className="rounded-md border px-2 py-1"
          >
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-600">
            Page {page} of {totalPages} • {total} total
          </span>
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border px-2 py-1 disabled:opacity-50">Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-md border px-2 py-1 disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );
}
